import { useState, useEffect, useMemo } from 'react'
import { useCurrentUser } from 'context/useCurrentUser'
import { fetchSchoolDetails, fetchCitiesByCountry, updateSchool } from '../service/schoolDetailsService'
import { US_STATES } from '../data/usStates'
import type { CityOption } from '../types'

// -- Pure helpers (no hooks, no state) --

function resolveStateCode(stateName: string | null): string | null {
  if (!stateName) return null
  const normalized = stateName.toLowerCase()
  const match = US_STATES.find(
    s => s.value.toLowerCase() === normalized || s.label.toLowerCase() === normalized
  )
  return match?.value || null
}

function stateCodeToLabel(code: string): string {
  return US_STATES.find(s => s.value === code)?.label || code
}

function filterCitiesByState(cities: CityOption[], stateCode: string): { value: string; label: string }[] {
  const label = stateCodeToLabel(stateCode)
  return cities
    .filter(c => (c.longName || '').includes(`, ${label},`))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(c => ({ value: c.id, label: c.name }))
}

// -- Hook --

interface FormState {
  name: string
  zipcode: string
  state: string | null
  cityId: string | null
}

export function useSchoolDetails() {
  const { currentUser } = useCurrentUser()
  const schoolId = currentUser?.selected_school?.id

  const [form, setForm] = useState<FormState>({ name: '', zipcode: '', state: null, cityId: null })
  const [allCities, setAllCities] = useState<CityOption[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [saving, setSaving] = useState(false)

  // Fetch school data
  useEffect(() => {
    if (!schoolId) return
    let cancelled = false

    async function load() {
      setLoading(true)
      try {
        const { school, countries, cities } = await fetchSchoolDetails(schoolId!)
        if (cancelled) return

        let loadedCities = cities
        if (loadedCities.length === 0) {
          const usCountry = countries.find(c => c.name === 'United States')
          if (usCountry) {
            loadedCities = await fetchCitiesByCountry(usCountry.id)
            if (cancelled) return
          }
        }

        setAllCities(loadedCities)
        setForm({
          name: school.name || '',
          zipcode: school.zipcode || '',
          state: resolveStateCode(school.stateName),
          cityId: school.cityId || null,
        })
      } catch (err) {
        console.error('[SchoolDetails] load failed:', err)
        if (!cancelled) setLoadError(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [schoolId])

  // Derived: cities filtered by selected state
  const cityOptions = useMemo(
    () => (form.state ? filterCitiesByState(allCities, form.state) : []),
    [form.state, allCities]
  )

  // -- Event handlers (no useCallback needed — this component rarely passes these as props to memoized children) --

  function updateField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  function handleStateChange(stateCode: string | null) {
    setForm(prev => ({ ...prev, state: stateCode, cityId: null }))
  }

  async function handleSave(): Promise<boolean> {
    // Validate
    const nextErrors: Record<string, string> = {}
    if (!form.name.trim()) nextErrors.name = 'School name is required'
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return false

    if (!schoolId) return false

    setSaving(true)
    try {
      await updateSchool(schoolId, {
        name: form.name,
        zipcode: form.zipcode,
        cityId: form.cityId,
      })
      return true
    } catch (err) {
      console.error('[SchoolDetails] save failed:', err)
      return false
    } finally {
      setSaving(false)
    }
  }

  return { form, errors, cityOptions, loading, loadError, saving, updateField, handleStateChange, handleSave }
}
